import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  Shop,
  Category,
  ShopType,
  Event,
  Floor,
  Slider,
  Marquee,
  Screensaver,
  Terminal,
  Tab,
  AuthResponse,
  ApiResponse,
  SearchRequest,
  AppSettings,
  UpdateAppSettingsRequest,
  RouteEntity,
} from "../types";

// Global callback for backend availability status
let backendUnavailableCallback: ((unavailable: boolean) => void) | null = null;

export function setBackendUnavailableCallback(
  callback: (unavailable: boolean) => void
) {
  backendUnavailableCallback = callback;
}

function isConnectionError(error: any): boolean {
  // Network errors (no response)
  if (!error.response) {
    return (
      error.code === "ECONNABORTED" || // Timeout
      error.code === "ERR_NETWORK" || // Network error
      error.message?.includes("Network Error") ||
      error.message?.includes("timeout") ||
      error.message?.includes("Failed to fetch")
    );
  }

  // Server errors (5xx) that indicate backend is down
  if (error.response?.status >= 500 && error.response?.status < 600) {
    return true;
  }

  return false;
}

class ApiService {
  private api: AxiosInstance;
  private readonly MEDIA_PATH = process.env.REACT_APP_MEDIA_PATH;
  private connectionErrorCount = 0;
  private readonly CONNECTION_ERROR_THRESHOLD = 2;
  private wasBackendUnavailable = false;

  constructor() {
    const apiUrl = process.env.REACT_APP_API_URL;

    if (!apiUrl) {
      console.error("❌ REACT_APP_API_URL is not set!");
      console.error("Please check your .env file and make sure it contains:");
      console.error("REACT_APP_API_URL=http://localhost:3001/api");
      console.error("Then restart the dev server (npm start)");
    } else {
      console.log("✅ API Base URL configured:", apiUrl);
    }

    // Ensure baseURL is always set, even if env var is missing
    // This prevents axios from making relative requests to the frontend
    this.api = axios.create({
      baseURL: apiUrl, // Fallback to prevent relative requests
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Log warning if using fallback
    if (!apiUrl) {
      console.warn("⚠️ Using fallback API URL: http://localhost:3001/api");
      console.warn(
        "⚠️ Please set REACT_APP_API_URL in .env file and restart the server"
      );
    }

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        const fullUrl = config.baseURL
          ? `${config.baseURL}${config.url}`
          : config.url;
        console.log("API Request:", {
          method: config.method?.toUpperCase(),
          url: fullUrl,
          baseURL: config.baseURL || "NOT SET (relative request)",
          hasToken: !!token,
        });

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check if we got HTML instead of JSON (wrong server)
        const contentType = response.headers["content-type"] || "";
        if (contentType.includes("text/html")) {
          console.error(
            "❌ Received HTML instead of JSON! Request went to wrong server."
          );
          console.error("Response URL:", response.config.url);
          console.error("Base URL:", response.config.baseURL);
          throw new Error(
            "Backend server not responding correctly. Check if backend is running on port 3001."
          );
        }

        // Reset error count on successful response
        if (this.connectionErrorCount > 0) {
          this.connectionErrorCount = 0;
          // If backend was unavailable and now it's back, reload the page
          if (this.wasBackendUnavailable && backendUnavailableCallback) {
            this.wasBackendUnavailable = false;
            backendUnavailableCallback(false);
            // Reload page after a short delay to ensure state is updated
            setTimeout(() => {
              window.location.reload();
            }, 100);
          } else if (backendUnavailableCallback) {
            backendUnavailableCallback(false);
          }
        }
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          // Don't redirect if we're on login page or home page
          const pathname = window.location.pathname;
          if (!pathname.includes("/admin/login") && pathname !== "/") {
            window.location.href = "/admin/login";
          }
          return Promise.reject(error);
        }

        // Check if it's a connection error
        if (isConnectionError(error)) {
          this.connectionErrorCount++;
          if (this.connectionErrorCount >= this.CONNECTION_ERROR_THRESHOLD) {
            this.wasBackendUnavailable = true;
            if (backendUnavailableCallback) {
              backendUnavailableCallback(true);
            }
          }
        } else {
          // Reset error count for non-connection errors
          this.connectionErrorCount = 0;
          if (backendUnavailableCallback) {
            backendUnavailableCallback(false);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  login = async (email: string, password: string): Promise<AuthResponse> => {
    const loginUrl = "/auth/login";
    const fullUrl = this.api.defaults.baseURL
      ? `${this.api.defaults.baseURL}${loginUrl}`
      : loginUrl;

    console.log("🔐 Login attempt:", {
      email,
      fullUrl,
      baseURL: this.api.defaults.baseURL || "NOT SET",
      willGoToBackend:
        !!this.api.defaults.baseURL &&
        this.api.defaults.baseURL.includes("localhost:3001"),
    });

    try {
      const response = await this.api.post<ApiResponse<AuthResponse>>(
        loginUrl,
        {
          email,
          password,
        }
      );
      console.log("✅ Login successful");
      return response.data.data!;
    } catch (error: any) {
      console.error("❌ Login failed:", {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: fullUrl,
      });
      throw error;
    }
  };

  async register(email: string, password: string): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>(
      "/auth/register",
      {
        email,
        password,
      }
    );
    return response.data.data!;
  }

  async getProfile(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>("/auth/profile");
    return response.data.data!;
  }

  // Shop endpoints
  async getShops(params?: {
    floor?: number;
    category?: string;
    tabId?: string;
    type?: string;
    language?: string;
    includeInactive?: boolean;
  }): Promise<Shop[]> {
    const response = await this.api.get<ApiResponse<Shop[]>>("/shops", {
      params: {
        ...params,
        includeInactive: params?.includeInactive ? "true" : undefined,
      },
    });
    return response.data.data!;
  }

  async getShopById(id: string): Promise<Shop> {
    const response = await this.api.get<ApiResponse<Shop>>(`/shops/${id}`);
    return response.data.data!;
  }

  async searchShops(searchRequest: SearchRequest): Promise<Shop[]> {
    const response = await this.api.post<ApiResponse<Shop[]>>(
      "/shops/search",
      searchRequest
    );
    return response.data.data!;
  }

  async createShop(shopData: Partial<Shop>): Promise<Shop> {
    const response = await this.api.post<ApiResponse<Shop>>("/shops", shopData);
    return response.data.data!;
  }

  async updateShop(id: string, shopData: Partial<Shop>): Promise<Shop> {
    const response = await this.api.put<ApiResponse<Shop>>(
      `/shops/${id}`,
      shopData
    );
    return response.data.data!;
  }

  async deleteShop(id: string): Promise<void> {
    console.log("[apiService.deleteShop] Called with ID:", id);
    console.log(
      "[apiService.deleteShop] Making DELETE request to:",
      `/shops/${id}`
    );
    try {
      const response = await this.api.delete(`/shops/${id}`);
      console.log(
        "[apiService.deleteShop] Request successful, response:",
        response
      );
      return response.data;
    } catch (error: any) {
      console.error("[apiService.deleteShop] Request failed:", error);
      console.error("[apiService.deleteShop] Error details:", {
        message: error?.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      throw error;
    }
  }

  async uploadShopLogo(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("shopLogo", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/shops/logo/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.data!;
  }

  async uploadMapIconInactive(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("mapIconInactive", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/shops/icons/inactive/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.data!;
  }

  async uploadMapIconActive(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("mapIconActive", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/shops/icons/active/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.data!;
  }

  async uploadShopPhotos(shopId: string, files: File[]): Promise<any[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("photos", file);
    });

    const response = await this.api.post<ApiResponse<any[]>>(
      `/shops/${shopId}/photos`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.data!;
  }

  async deleteShopPhoto(photoId: string): Promise<void> {
    await this.api.delete(`/shops/photos/${photoId}`);
  }

  // Category endpoints
  async getCategories(tabId?: number): Promise<Category[]> {
    const response = await this.api.get<ApiResponse<Category[]>>(
      "/categories",
      {
        params: tabId ? { tabId } : undefined,
      }
    );
    return response.data.data!;
  }

  // Shop type endpoints
  async getShopTypes(params?: { language?: string }): Promise<ShopType[]> {
    const response = await this.api.get<ApiResponse<ShopType[]>>(
      "/shop-types",
      {
        params,
      }
    );
    return response.data.data!;
  }

  async getCategoryById(id: string): Promise<Category> {
    const response = await this.api.get<ApiResponse<Category>>(
      `/categories/${id}`
    );
    return response.data.data!;
  }

  async createCategory(categoryData: Partial<Category>): Promise<Category> {
    const response = await this.api.post<ApiResponse<Category>>(
      "/categories",
      categoryData
    );
    return response.data.data!;
  }

  async updateCategory(
    id: string,
    categoryData: Partial<Category>
  ): Promise<Category> {
    const response = await this.api.put<ApiResponse<Category>>(
      `/categories/${id}`,
      categoryData
    );
    return response.data.data!;
  }

  async deleteCategory(id: string): Promise<any> {
    const response = await this.api.delete(`/categories/${id}`);
    return response.data;
  }

  async updateCategoriesOrder(
    categories: { id: string; sortOrder: number }[]
  ): Promise<void> {
    await this.api.put("/categories/order", { categories });
  }

  // Event endpoints
  async getEvents(params?: {
    active?: boolean;
    shopId?: string;
  }): Promise<Event[]> {
    const response = await this.api.get<ApiResponse<any[]>>("/events", {
      params,
    });
    const raw = response.data.data || [];
    // Map backend Prisma model to frontend Event type
    const mapped: Event[] = raw.map((e: any) => ({
      id: String(e.id),
      nameRu: e?.name?.ru ?? "",
      nameEn: e?.name?.en ?? "",
      shortDescriptionRu: e?.shortDescription?.ru ?? "",
      shortDescriptionEn: e?.shortDescription?.en ?? "",
      fullDescriptionRu: e?.fullDescription?.ru ?? "",
      fullDescriptionEn: e?.fullDescription?.en ?? "",
      locationRu: e?.location?.ru ?? "",
      locationEn: e?.location?.en ?? "",
      workingHoursRu: e?.workingHours?.ru ?? "",
      workingHoursEn: e?.workingHours?.en ?? "",
      searchKeywords: e.searchKeywords ?? "",
      socialMedia: e.socialMedia ?? undefined,
      logoUrl: e.logoUrl ?? "",
      isActive: Boolean(e.isActive),
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      shopId: e.shopId ? Number(e.shopId) : undefined,
      shop: e.shop
        ? {
            id: String(e.shop.id),
            nameRu: e.shop.name?.ru ?? "",
            nameEn: e.shop.name?.en ?? "",
            logoUrl: e.shop.logoUrl ?? "",
            floor: e.shop.floor ?? 0,
            locationRu: e.shop.location?.ru ?? "",
            locationEn: e.shop.location?.en ?? "",
          }
        : undefined,
      photos:
        e.photos?.map((p: any) => ({
          id: String(p.id),
          url: p.url,
          eventId: String(p.eventId),
          createdAt: p.createdAt,
        })) ?? [],
    }));
    return mapped;
  }

  async getEventById(id: string): Promise<Event> {
    const response = await this.api.get<ApiResponse<any>>(`/events/${id}`);
    const e = response.data.data!;
    return {
      id: String(e.id),
      nameRu: e?.name?.ru ?? "",
      nameEn: e?.name?.en ?? "",
      shortDescriptionRu: e?.shortDescription?.ru ?? "",
      shortDescriptionEn: e?.shortDescription?.en ?? "",
      fullDescriptionRu: e?.fullDescription?.ru ?? "",
      fullDescriptionEn: e?.fullDescription?.en ?? "",
      locationRu: e?.location?.ru ?? "",
      locationEn: e?.location?.en ?? "",
      workingHoursRu: e?.workingHours?.ru ?? "",
      workingHoursEn: e?.workingHours?.en ?? "",
      searchKeywords: e.searchKeywords ?? "",
      socialMedia: e.socialMedia ?? undefined,
      logoUrl: e.logoUrl ?? "",
      isActive: Boolean(e.isActive),
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      shopId: e.shopId ? Number(e.shopId) : undefined,
      shop: e.shop
        ? {
            id: String(e.shop.id),
            nameRu: e.shop.name?.ru ?? "",
            nameEn: e.shop.name?.en ?? "",
            logoUrl: e.shop.logoUrl ?? "",
            floor: e.shop.floor ?? 0,
            locationRu: e.shop.location?.ru ?? "",
            locationEn: e.shop.location?.en ?? "",
          }
        : undefined,
      photos:
        e.photos?.map((p: any) => ({
          id: String(p.id),
          url: p.url,
          eventId: String(p.eventId),
          createdAt: p.createdAt,
        })) ?? [],
    };
  }

  async createEvent(eventData: Partial<Event>): Promise<Event> {
    // Map frontend Event to backend format
    const payload: any = {
      name: {
        ru: eventData.nameRu ?? "",
        en: eventData.nameEn || undefined,
      },
      shortDescription:
        eventData.shortDescriptionRu || eventData.shortDescriptionEn
          ? {
              ru: eventData.shortDescriptionRu || "",
              en: eventData.shortDescriptionEn || undefined,
            }
          : undefined,
      fullDescription:
        eventData.fullDescriptionRu || eventData.fullDescriptionEn
          ? {
              ru: eventData.fullDescriptionRu || "",
              en: eventData.fullDescriptionEn || undefined,
            }
          : undefined,
      location:
        eventData.locationRu || eventData.locationEn
          ? {
              ru: eventData.locationRu || "",
              en: eventData.locationEn || undefined,
            }
          : undefined,
      workingHours:
        eventData.workingHoursRu || eventData.workingHoursEn
          ? {
              ru: eventData.workingHoursRu || "",
              en: eventData.workingHoursEn || undefined,
            }
          : undefined,
      searchKeywords: eventData.searchKeywords || undefined,
      socialMedia: eventData.socialMedia || undefined,
      logoUrl: eventData.logoUrl || undefined,
      shopId: eventData.shopId || undefined,
      isActive: eventData.isActive !== undefined ? eventData.isActive : true,
    };
    const response = await this.api.post<ApiResponse<any>>("/events", payload);
    const e = response.data.data!;
    return {
      id: String(e.id),
      nameRu: e?.name?.ru ?? "",
      nameEn: e?.name?.en ?? "",
      shortDescriptionRu: e?.shortDescription?.ru ?? "",
      shortDescriptionEn: e?.shortDescription?.en ?? "",
      fullDescriptionRu: e?.fullDescription?.ru ?? "",
      fullDescriptionEn: e?.fullDescription?.en ?? "",
      locationRu: e?.location?.ru ?? "",
      locationEn: e?.location?.en ?? "",
      workingHoursRu: e?.workingHours?.ru ?? "",
      workingHoursEn: e?.workingHours?.en ?? "",
      searchKeywords: e.searchKeywords ?? "",
      socialMedia: e.socialMedia ?? undefined,
      logoUrl: e.logoUrl ?? "",
      isActive: Boolean(e.isActive),
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      shopId: e.shopId ? Number(e.shopId) : undefined,
      shop: e.shop
        ? {
            id: String(e.shop.id),
            nameRu: e.shop.name?.ru ?? "",
            nameEn: e.shop.name?.en ?? "",
            logoUrl: e.shop.logoUrl ?? "",
            floor: e.shop.floor ?? 0,
            locationRu: e.shop.location?.ru ?? "",
            locationEn: e.shop.location?.en ?? "",
          }
        : undefined,
      photos:
        e.photos?.map((p: any) => ({
          id: String(p.id),
          url: p.url,
          eventId: String(p.eventId),
          createdAt: p.createdAt,
        })) ?? [],
    };
  }

  async updateEvent(id: string, eventData: Partial<Event>): Promise<Event> {
    // Map frontend Event to backend format
    const payload: any = {};
    if (eventData.nameRu !== undefined || eventData.nameEn !== undefined) {
      payload.name = {
        ru: eventData.nameRu ?? "",
        en: eventData.nameEn || undefined,
      };
    }
    // Always include these fields if they are provided (even if empty) to allow clearing
    if (
      eventData.shortDescriptionRu !== undefined ||
      eventData.shortDescriptionEn !== undefined
    ) {
      payload.shortDescription = {
        ru: eventData.shortDescriptionRu ?? "",
        en:
          eventData.shortDescriptionEn !== undefined
            ? eventData.shortDescriptionEn || undefined
            : undefined,
      };
    }
    if (
      eventData.fullDescriptionRu !== undefined ||
      eventData.fullDescriptionEn !== undefined
    ) {
      payload.fullDescription = {
        ru: eventData.fullDescriptionRu ?? "",
        en:
          eventData.fullDescriptionEn !== undefined
            ? eventData.fullDescriptionEn || undefined
            : undefined,
      };
    }
    if (
      eventData.locationRu !== undefined ||
      eventData.locationEn !== undefined
    ) {
      payload.location = {
        ru: eventData.locationRu ?? "",
        en:
          eventData.locationEn !== undefined
            ? eventData.locationEn || undefined
            : undefined,
      };
    }
    if (
      eventData.workingHoursRu !== undefined ||
      eventData.workingHoursEn !== undefined
    ) {
      payload.workingHours = {
        ru: eventData.workingHoursRu ?? "",
        en:
          eventData.workingHoursEn !== undefined
            ? eventData.workingHoursEn || undefined
            : undefined,
      };
    }
    if (eventData.searchKeywords !== undefined) {
      payload.searchKeywords = eventData.searchKeywords;
    }
    if (eventData.socialMedia !== undefined) {
      payload.socialMedia = eventData.socialMedia;
    }
    if (eventData.logoUrl !== undefined) {
      payload.logoUrl = eventData.logoUrl;
    }
    if (eventData.shopId !== undefined) {
      payload.shopId = eventData.shopId;
    }
    if (eventData.isActive !== undefined) {
      payload.isActive = eventData.isActive;
    }

    const response = await this.api.put<ApiResponse<any>>(
      `/events/${id}`,
      payload
    );
    const e = response.data.data!;
    return {
      id: String(e.id),
      nameRu: e?.name?.ru ?? "",
      nameEn: e?.name?.en ?? "",
      shortDescriptionRu: e?.shortDescription?.ru ?? "",
      shortDescriptionEn: e?.shortDescription?.en ?? "",
      fullDescriptionRu: e?.fullDescription?.ru ?? "",
      fullDescriptionEn: e?.fullDescription?.en ?? "",
      locationRu: e?.location?.ru ?? "",
      locationEn: e?.location?.en ?? "",
      workingHoursRu: e?.workingHours?.ru ?? "",
      workingHoursEn: e?.workingHours?.en ?? "",
      searchKeywords: e.searchKeywords ?? "",
      socialMedia: e.socialMedia ?? undefined,
      logoUrl: e.logoUrl ?? "",
      isActive: Boolean(e.isActive),
      createdAt: e.createdAt,
      updatedAt: e.updatedAt,
      shopId: e.shopId ? Number(e.shopId) : undefined,
      shop: e.shop
        ? {
            id: String(e.shop.id),
            nameRu: e.shop.name?.ru ?? "",
            nameEn: e.shop.name?.en ?? "",
            logoUrl: e.shop.logoUrl ?? "",
            floor: e.shop.floor ?? 0,
            locationRu: e.shop.location?.ru ?? "",
            locationEn: e.shop.location?.en ?? "",
          }
        : undefined,
      photos:
        e.photos?.map((p: any) => ({
          id: String(p.id),
          url: p.url,
          eventId: String(p.eventId),
          createdAt: p.createdAt,
        })) ?? [],
    };
  }

  async deleteEvent(id: string): Promise<void> {
    await this.api.delete(`/events/${id}`);
  }

  async uploadEventLogo(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("eventLogo", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/events/logo/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.data!;
  }

  async uploadEventPhotos(eventId: string, files: File[]): Promise<any[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("eventPhotos", file);
    });

    const response = await this.api.post<ApiResponse<any[]>>(
      `/events/${eventId}/photos`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data.data!;
  }

  async deleteEventPhoto(photoId: string): Promise<void> {
    await this.api.delete(`/events/photos/${photoId}`);
  }

  // Floor endpoints
  async getFloors(): Promise<Floor[]> {
    const response = await this.api.get<ApiResponse<Floor[]>>("/floors");
    return response.data.data!;
  }

  async getFloorById(id: string): Promise<Floor> {
    const response = await this.api.get<ApiResponse<Floor>>(`/floors/${id}`);
    return response.data.data!;
  }

  async createFloor(floorData: Partial<Floor>): Promise<Floor> {
    const response = await this.api.post<ApiResponse<Floor>>(
      "/floors",
      floorData
    );
    return response.data.data!;
  }

  async updateFloor(id: string, floorData: Partial<Floor>): Promise<Floor> {
    const response = await this.api.put<ApiResponse<Floor>>(
      `/floors/${id}`,
      floorData
    );
    return response.data.data!;
  }

  async deleteFloor(id: string): Promise<void> {
    await this.api.delete(`/floors/${id}`);
  }

  async uploadFloorMap(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append("floorMap", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/floors/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.data!;
  }

  // Slider endpoints
  async getSliders(params?: { active?: boolean }): Promise<Slider[]> {
    const response = await this.api.get<ApiResponse<Slider[]>>("/slider", {
      params,
    });

    console.log("Raw slider API response:", response.data.data);

    // Build full URLs using MEDIA_PATH
    const sliders = response.data.data!.map((slider) => ({
      ...slider,
      url: slider.url.startsWith("http")
        ? slider.url
        : `${this.MEDIA_PATH}${slider.url.startsWith("/") ? "" : "/"}${
            slider.url
          }`,
    }));

    return sliders;
  }

  async getSliderById(id: string): Promise<Slider> {
    const response = await this.api.get<ApiResponse<Slider>>(`/slider/${id}`);

    // Build full URLs using MEDIA_PATH
    const slider = {
      ...response.data.data!,
      url: response.data.data!.url.startsWith("http")
        ? response.data.data!.url
        : `${this.MEDIA_PATH}${
            response.data.data!.url.startsWith("/") ? "" : "/"
          }${response.data.data!.url}`,
    };

    return slider;
  }

  createSlider = async (sliderData: Partial<Slider>): Promise<Slider> => {
    const response = await this.api.post<ApiResponse<Slider>>(
      "/slider",
      sliderData
    );
    return response.data.data!;
  };

  updateSlider = async (
    id: string,
    sliderData: Partial<Slider>
  ): Promise<Slider> => {
    const response = await this.api.put<ApiResponse<Slider>>(
      `/slider/${id}`,
      sliderData
    );
    return response.data.data!;
  };

  deleteSlider = async (id: string): Promise<void> => {
    await this.api.delete(`/slider/${id}`);
  };

  uploadSliderMedia = async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("slider", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/slider/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Return relative path only, full URL will be built when displaying
    return { url: response.data.data!.url };
  };

  // Marquee endpoints
  async getMarquees(params?: { active?: boolean }): Promise<Marquee[]> {
    const response = await this.api.get<ApiResponse<any[]>>("/marquee", {
      params,
    });
    const raw = response.data.data || [];
    // Map backend Prisma model { id:number, text: {ru,en}, isActive, createdAt, updatedAt }
    const mapped: Marquee[] = raw.map((m: any) => ({
      id: String(m.id),
      textRu: m?.text?.ru ?? "",
      textEn: m?.text?.en ?? "",
      isActive: Boolean(m.isActive),
      createdAt: m.createdAt,
      updatedAt: m.updatedAt,
    }));
    return mapped;
  }

  async getMarqueeById(id: string): Promise<Marquee> {
    const response = await this.api.get<ApiResponse<any>>(`/marquee/${id}`);
    const m = response.data.data!;
    return {
      id: String(m.id),
      textRu: m?.text?.ru ?? "",
      textEn: m?.text?.en ?? "",
      isActive: Boolean(m.isActive),
      createdAt: m.createdAt,
      updatedAt: m.updatedAt,
    };
  }

  createMarquee = async (data: Partial<Marquee>): Promise<Marquee> => {
    const payload: any = {
      text: { ru: data.textRu ?? "", en: data.textEn ?? "" },
      isActive: data.isActive ?? true,
    };
    const response = await this.api.post<ApiResponse<any>>("/marquee", payload);
    const m = response.data.data!;
    return {
      id: String(m.id),
      textRu: m?.text?.ru ?? "",
      textEn: m?.text?.en ?? "",
      isActive: Boolean(m.isActive),
      createdAt: m.createdAt,
      updatedAt: m.updatedAt,
    };
  };

  updateMarquee = async (
    id: string,
    data: Partial<Marquee>
  ): Promise<Marquee> => {
    const payload: any = {
      text: { ru: data.textRu ?? "", en: data.textEn ?? "" },
      isActive: data.isActive,
    };
    const response = await this.api.put<ApiResponse<any>>(
      `/marquee/${id}`,
      payload
    );
    const m = response.data.data!;
    return {
      id: String(m.id),
      textRu: m?.text?.ru ?? "",
      textEn: m?.text?.en ?? "",
      isActive: Boolean(m.isActive),
      createdAt: m.createdAt,
      updatedAt: m.updatedAt,
    };
  };

  deleteMarquee = async (id: string): Promise<void> => {
    await this.api.delete(`/marquee/${id}`);
  };

  // Screensaver endpoints
  async getScreensavers(params?: { active?: boolean }): Promise<Screensaver[]> {
    const response = await this.api.get<ApiResponse<Screensaver[]>>(
      "/screensaver",
      { params }
    );

    console.log("Raw API response:", response.data.data);

    // Build full URLs using MEDIA_PATH
    const screensavers = response.data.data!.map((screensaver) => ({
      ...screensaver,
      url: screensaver.url.startsWith("http")
        ? screensaver.url
        : `${this.MEDIA_PATH}${screensaver.url.startsWith("/") ? "" : "/"}${
            screensaver.url
          }`,
    }));

    return screensavers;
  }

  async getScreensaverById(id: string): Promise<Screensaver> {
    const response = await this.api.get<ApiResponse<Screensaver>>(
      `/screensaver/${id}`
    );

    // Build full URLs using MEDIA_PATH
    const screensaver = {
      ...response.data.data!,
      url: response.data.data!.url.startsWith("http")
        ? response.data.data!.url
        : `${this.MEDIA_PATH}${
            response.data.data!.url.startsWith("/") ? "" : "/"
          }${response.data.data!.url}`,
    };

    return screensaver;
  }

  createScreensaver = async (
    screensaverData: Partial<Screensaver>
  ): Promise<Screensaver> => {
    const response = await this.api.post<ApiResponse<Screensaver>>(
      "/screensaver",
      screensaverData
    );
    return response.data.data!;
  };

  updateScreensaver = async (
    id: string,
    screensaverData: Partial<Screensaver>
  ): Promise<Screensaver> => {
    const response = await this.api.put<ApiResponse<Screensaver>>(
      `/screensaver/${id}`,
      screensaverData
    );
    return response.data.data!;
  };

  deleteScreensaver = async (id: string): Promise<void> => {
    await this.api.delete(`/screensaver/${id}`);
  };

  uploadScreensaverMedia = async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("screensaver", file);

    const response = await this.api.post<ApiResponse<{ url: string }>>(
      "/screensaver/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Return relative path only, full URL will be built when displaying
    return { url: response.data.data!.url };
  };

  // Terminal endpoints
  async getTerminals(params?: {
    active?: boolean;
    floor?: number;
  }): Promise<Terminal[]> {
    const response = await this.api.get<ApiResponse<Terminal[]>>("/terminals", {
      params,
    });
    return response.data.data!;
  }

  async getTerminalById(id: string): Promise<Terminal> {
    const response = await this.api.get<ApiResponse<Terminal>>(
      `/terminals/${id}`
    );
    return response.data.data!;
  }

  async createTerminal(terminalData: Partial<Terminal>): Promise<Terminal> {
    const response = await this.api.post<ApiResponse<Terminal>>(
      "/terminals",
      terminalData
    );
    return response.data.data!;
  }

  async updateTerminal(
    id: string,
    terminalData: Partial<Terminal>
  ): Promise<Terminal> {
    const response = await this.api.put<ApiResponse<Terminal>>(
      `/terminals/${id}`,
      terminalData
    );
    return response.data.data!;
  }

  async deleteTerminal(id: string): Promise<void> {
    await this.api.delete(`/terminals/${id}`);
  }

  // Tab endpoints
  async getTabs(): Promise<Tab[]> {
    const response = await this.api.get<ApiResponse<Tab[]>>("/tabs");
    return response.data.data!;
  }

  async getTabById(id: string): Promise<Tab> {
    const response = await this.api.get<ApiResponse<Tab>>(`/tabs/${id}`);
    return response.data.data!;
  }

  async createTab(tabData: Partial<Tab>): Promise<Tab> {
    const response = await this.api.post<ApiResponse<Tab>>(`/tabs`, tabData);
    return response.data.data!;
  }

  async updateTab(id: string, tabData: Partial<Tab>): Promise<Tab> {
    const response = await this.api.put<ApiResponse<Tab>>(
      `/tabs/${id}`,
      tabData
    );
    return response.data.data!;
  }

  async deleteTab(id: string): Promise<void> {
    await this.api.delete(`/tabs/${id}`);
  }

  async updateTabsOrder(
    tabs: { id: string; sortOrder: number }[]
  ): Promise<void> {
    await this.api.put("/tabs/order", { tabs });
  }

  // Settings endpoints
  getAppSettings = async (): Promise<AppSettings> => {
    const response = await this.api.get<ApiResponse<AppSettings>>("/settings");
    return response.data.data!;
  };

  updateAppSettings = async (
    settings: UpdateAppSettingsRequest
  ): Promise<AppSettings> => {
    console.log("Updating app settings:", settings);
    console.log("Current token:", localStorage.getItem("token"));
    try {
      const response = await this.api.put<ApiResponse<AppSettings>>(
        "/settings",
        settings
      );
      console.log("Settings update response:", response.data);
      return response.data.data!;
    } catch (error) {
      console.error("Settings update error:", error);
      throw error;
    }
  };

  // Route endpoints
  async getRoutes(): Promise<RouteEntity[]> {
    const response = await this.api.get<ApiResponse<RouteEntity[]>>("/routes");
    const routes = response.data.data!;
    console.log("API getRoutes response:", routes);
    return routes;
  }

  async getRouteById(id: string): Promise<RouteEntity> {
    const response = await this.api.get<ApiResponse<RouteEntity>>(
      `/routes/${id}`
    );
    return response.data.data!;
  }

  async createRoute(routeData: Partial<RouteEntity>): Promise<RouteEntity> {
    const response = await this.api.post<ApiResponse<RouteEntity>>(
      "/routes",
      routeData
    );
    return response.data.data!;
  }

  async updateRoute(
    id: string,
    routeData: Partial<RouteEntity>
  ): Promise<RouteEntity> {
    const response = await this.api.put<ApiResponse<RouteEntity>>(
      `/routes/${id}`,
      routeData
    );
    return response.data.data!;
  }

  async deleteRoute(id: string): Promise<void> {
    await this.api.delete(`/routes/${id}`);
  }
}

export const apiService = new ApiService();
export default apiService;
